import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { GameResult } from "../../api";
import { useCustomColors } from "../../hooks/useCustomColors";
import { useTranslation } from "react-i18next";

interface ResultsStripProps {
  results: GameResult[];
  positionCount?: number;
  liveReveal?: {
    game_number: string;
    numbers: number[];
    revealedCount: number;
  } | null;
}

/** Arrow/ribbon outline: the gold edge follows the point, no negative margins. */
const RIBBON_CLIP =
  "polygon(0 0, calc(100% - 13px) 0, 100% 50%, calc(100% - 13px) 100%, 0 100%)";

/** Bevel layer: lit crown, shaded belly. Alpha only, so it rides any ribbon colour. */
const RIBBON_SHEEN =
  "linear-gradient(180deg, rgba(255, 255, 255, 0.34) 0%, rgba(255, 255, 255, 0.10) 44%, rgba(0, 0, 0, 0.14) 56%, rgba(0, 0, 0, 0.38) 100%)";

const ResultsStrip = ({ results, positionCount, liveReveal = null }: ResultsStripProps) => {
  const colors = useCustomColors();
  const { t } = useTranslation();
  const displayResults = liveReveal
    ? [
        { game_number: liveReveal.game_number, numbers: liveReveal.numbers },
        ...results.filter((result) => result.game_number !== liveReveal.game_number),
      ]
    : results;
  const count = positionCount ?? Math.max(0, ...displayResults.map((result) => result.numbers.length));
  const positions = Array.from({ length: count }, (_, index) => index + 1);

  const headCellSx = {
    height: { xs: "52px", md: "56px" },
    py: 0,
    px: { xs: "16px", md: "20px" },
    color: colors.table.headText,
    backgroundColor: colors.table.headBg,
    borderBottom: `1px solid ${colors.table.headDivider}`,
    fontSize: "10px",
    fontWeight: 800,
    letterSpacing: "1px",
    whiteSpace: "nowrap" as const,
    "&:last-of-type": { pr: "24px" },
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        borderRadius: "16px",
        overflow: "hidden",
        border: `1px solid ${colors.table.border}`,
        background: colors.table.surface,
        boxShadow: colors.table.frame,
      }}
    >
      <TableContainer
        sx={{
          width: "100%",
          maxWidth: "100%",
          // The scrollbar belongs to this wrapper only: never sticky, never the page's.
          overflowX: "auto",
          overflowY: "hidden",
          WebkitOverflowScrolling: "touch",
          overscrollBehaviorX: "contain",
        }}
      >
        <Table sx={{ minWidth: Math.max(600, 150 + count * 78), tableLayout: "fixed" }}>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                ...headCellSx,
                position: "sticky",
                left: 0,
                zIndex: 3,
                width: "150px",
                letterSpacing: "2px",
                boxShadow: colors.table.stickyShadow,
              }}
            >
              {t("lotto_game")}
            </TableCell>
            {positions.map((position) => (
              <TableCell key={position} align="center" sx={headCellSx}>
                {t("result_position", { position })}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {displayResults.map((result, rowIndex) => {
            const rowBg = rowIndex % 2 === 0 ? colors.table.rowOdd : colors.table.rowEven;
            const isLiveReveal = liveReveal?.game_number === result.game_number;
            const isNewest = rowIndex === 0 && !isLiveReveal;

            return (
              <TableRow
                key={result.game_number}
                className={isLiveReveal ? "reveal-row-live" : undefined}
                sx={{
                  backgroundColor: rowBg,
                  ...(isLiveReveal
                    ? {
                        backgroundImage: `linear-gradient(105deg, transparent 20%, ${colors.tint.gold} 48%, transparent 76%)`,
                        backgroundSize: "240% 100%",
                      }
                    : {}),
                }}
              >
                <TableCell
                  sx={{
                    position: "sticky",
                    left: 0,
                    zIndex: 2,
                    height: { xs: "58px", md: "64px" },
                    py: 0,
                    pl: { xs: "16px", md: "20px" },
                    pr: 0,
                    backgroundColor: isLiveReveal ? colors.table.headBg : rowBg,
                    borderBottom: `1px solid ${colors.table.rowDivider}`,
                    borderLeft: isLiveReveal ? `3px solid ${colors.accent.gold}` : "none",
                    boxShadow: colors.table.stickyShadow,
                  }}
                >
                  <Box
                    className={`lotto-ribbon${isLiveReveal ? " reveal-ribbon-live" : isNewest ? " led-ribbon-idle" : ""}`}
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      display: "inline-block",
                      p: "1px",
                      borderRadius: "6px 0 0 6px",
                      background: isLiveReveal
                        ? colors.gradient.goldShine
                        : isNewest
                          ? `linear-gradient(90deg, ${colors.accent.danger} 0%, #FF6B6B 45%, #FFD9D9 50%, #FF6B6B 55%, ${colors.accent.danger} 100%)`
                          : colors.table.ribbonEdge,
                      backgroundSize: isLiveReveal || isNewest ? "240% 100%" : undefined,
                      clipPath: RIBBON_CLIP,
                    }}
                  >
                    <Box
                      sx={{
                        py: "8px",
                        pl: "14px",
                        pr: "24px",
                        borderRadius: "5px 0 0 5px",
                        background: `${RIBBON_SHEEN}, ${isLiveReveal ? colors.tint.goldStrong : colors.table.ribbon}`,
                        clipPath: RIBBON_CLIP,
                        color: isLiveReveal ? colors.text.primary : colors.table.ribbonText,
                        fontSize: "12px",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                        textShadow: isLiveReveal ? colors.glow.gold : "0 1px 1px rgba(0, 0, 0, 0.55)",
                      }}
                    >
                      {result.game_number}
                    </Box>
                  </Box>
                </TableCell>
                {positions.map((position) => {
                  const landed = !isLiveReveal || position <= (liveReveal?.revealedCount ?? 0);
                  const value = landed ? result.numbers[position - 1] : undefined;
                  const cellKey = `${result.game_number}-${position}-${isLiveReveal ? value ?? "hidden" : "stable"}`;
                  const cascade = isNewest;
                  const popDelay = (position - 1) * 182;

                  return <TableCell
                    key={cellKey}
                    align="center"
                    sx={{
                      height: { xs: "58px", md: "64px" },
                      py: 0,
                      px: { xs: "8px", md: "12px" },
                      borderBottom: `1px solid ${colors.table.rowDivider}`,
                      "&:last-of-type": { pr: "24px" },
                    }}
                  >
                    <Box
                      className={`result-number lotto-ball ${isLiveReveal ? (landed ? "reveal-number-live" : "reveal-number-hidden") : cascade ? "reveal-number-cascade" : ""}`}
                      style={cascade ? { animationDelay: `${popDelay}ms, ${popDelay}ms, ${popDelay + 1170}ms` } : undefined}
                      sx={{
                        width: { xs: "36px", md: "40px" },
                        height: { xs: "36px", md: "40px" },
                        flexShrink: 0,
                        mx: "auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        perspective: "600px",
                        borderRadius: "50%",
                        background: colors.table.numberBg,
                        border: `2px solid ${colors.table.numberBorder}`,
                        boxShadow: colors.table.numberGlow,
                        color: colors.table.numberText,
                        fontSize: { xs: "14px", md: "16px" },
                        fontWeight: 800,
                      }}
                    >
                      {value === undefined ? "-" : <Box component="span" className="reveal-number-flip">{value}</Box>}
                    </Box>
                  </TableCell>
                })}
              </TableRow>
            );
          })}
        </TableBody>
        </Table>
      </TableContainer>
      {displayResults.length === 0 && <Typography sx={{ p: "20px", color: colors.text.secondary, fontSize: "13px" }}>{t("common_no_data")}</Typography>}
    </Box>
  );
};

export default ResultsStrip;
